"use client";

import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

import { AgentAvatar } from "@/shared/ui/agent-avatar";
import { cn } from "@/shared/lib/utils/cn";
import { InsightsAnswer } from "./InsightsAnswer";
import type { InvestigationTaskSection } from "./group-investigation-tasks";
import { InsightsLedgerCard } from "./InsightsLedgerCard";
import { InsightsToolStack } from "./InsightsToolStack";

/**
 * Flat expandable task row in the investigation feed.
 * Caller: InsightsChat task sections. Status/ticks/timing live in the Tasks
 * rail — chat headers are title + trail volume (tools/notes) only.
 * Open while the investigation is live; collapse when the run finishes.
 * Timeline border removed to match flow-builder avatar gutter.
 */
export function InsightsTaskSection({
  section,
  forceExpanded,
}: {
  section: InvestigationTaskSection;
  /** Keep open while the investigation is running. */
  forceExpanded?: boolean;
}) {
  const forcedOpen = Boolean(forceExpanded || section.active);
  const [open, setOpen] = useState(forcedOpen);
  const [prevForced, setPrevForced] = useState(forcedOpen);
  if (forcedOpen !== prevForced) {
    setPrevForced(forcedOpen);
    setOpen(forcedOpen);
  }

  const summaryParts: string[] = [section.title];
  if (!open) {
    if (section.toolCount) {
      summaryParts.push(`${section.toolCount} tool${section.toolCount === 1 ? "" : "s"}`);
    }
    if (section.noteCount) {
      summaryParts.push(`${section.noteCount} note${section.noteCount === 1 ? "" : "s"}`);
    }
  }

  return (
    <div id={section.id} data-task-id={section.taskId} className="w-full min-w-0 scroll-mt-3">
      <button
        type="button"
        className={cn(
          "flex w-full items-start gap-2 rounded-lg px-1 py-1.5 text-left text-[13px] transition-colors hover:bg-muted/40",
          (section.active || section.status === "in_progress") && "bg-primary/5",
        )}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
        )}
        <span
          className={cn(
            "min-w-0 flex-1 leading-snug font-medium",
            open ? "text-foreground" : "text-foreground/85",
          )}
        >
          {open ? section.title : summaryParts.join(" · ")}
        </span>
      </button>
      {open ? (
        <div className="mt-1 space-y-3 py-1">
          {section.children.map((child) => {
            if (child.kind === "user") {
              return (
                <div key={child.id} className="flex w-full min-w-0 justify-end">
                  <div className="overlay-chat-user-bubble max-w-[90%]">
                    <p className="whitespace-pre-wrap">{child.text}</p>
                  </div>
                </div>
              );
            }
            if (child.kind === "tool-stack") {
              return (
                <div key={child.row.id} className="flex w-full min-w-0 items-start gap-3">
                  <AgentAvatar />
                  <div className="min-w-0 flex-1">
                    <InsightsToolStack activities={child.row.activities} live={child.row.live} />
                  </div>
                </div>
              );
            }
            if (child.kind === "ledger") {
              return (
                <div key={child.id} className="flex w-full min-w-0 items-start gap-3">
                  <AgentAvatar />
                  <div className="min-w-0 flex-1">
                    <InsightsLedgerCard content={child.content} live={Boolean(child.live)} />
                  </div>
                </div>
              );
            }
            return (
              <div key={child.id} className="flex w-full min-w-0 items-start gap-3">
                <AgentAvatar />
                <div className="min-w-0 flex-1 overflow-hidden rounded-tl-md rounded-tr-2xl rounded-b-2xl border border-border/70 bg-card shadow-sm">
                  <InsightsAnswer text={child.text} streaming={child.streaming} />
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
